import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { useListLeasesQuery } from "../../../store/api/leasesApi";
import type { MaintenanceRequest } from "../../../store/api/maintenanceApi";
import { useListMaintenanceRequestsQuery } from "../../../store/api/maintenanceApi";
import type { Payment } from "../../../store/api/paymentsApi";
import { useListPaymentsQuery } from "../../../store/api/paymentsApi";
import type { Property } from "../../../store/api/propertiesApi";

interface PropertyDetailPanelProps {
	property: Property | null;
	isOpen: boolean;
	onClose: () => void;
	onEdit: (property: Property) => void;
}

const PropertyDetailPanel: React.FC<PropertyDetailPanelProps> = ({
	property,
	isOpen,
	onClose,
	onEdit,
}) => {
	const [activeTab, setActiveTab] = useState("overview");
	const [currentImageIndex, setCurrentImageIndex] = useState(0);

	// Reset image index when property changes or modal opens
	useEffect(() => {
		if (isOpen && property) {
			setCurrentImageIndex(0);
		}
	}, [property?.id, isOpen]);

	// Fetch real data for the property with error handling
	const { data: leasesData, error: leasesError } = useListLeasesQuery(
		{ property: property?.id, page_size: 100 },
		{ skip: !property?.id || !isOpen },
	);

	const { data: maintenanceData, error: maintenanceError } =
		useListMaintenanceRequestsQuery(
			{ property: property?.id, page_size: 1000 },
			{ skip: !property?.id || !isOpen },
		);

	// Get lease IDs for this property to filter payments
	const propertyLeaseIds = useMemo(() => {
		return leasesData?.results?.map((l) => l.id) || [];
	}, [leasesData]);

	// Fetch payments for leases of this property
	// Note: Backend only supports filtering by single lease_id, so we fetch all and filter client-side
	const { data: paymentsData, error: paymentsError } = useListPaymentsQuery(
		{ page_size: 1000 },
		{ skip: !property?.id || !isOpen },
	);

	// Log errors for debugging
	if (leasesError) {
		console.error("Error fetching leases:", leasesError);
	}
	if (maintenanceError) {
		console.error("Error fetching maintenance:", maintenanceError);
	}
	if (paymentsError) {
		console.error("Error fetching payments:", paymentsError);
	}

	// Filter payments to only those for this property's leases
	const propertyPayments = useMemo(() => {
		if (!paymentsData?.results || propertyLeaseIds.length === 0) return [];
		return paymentsData.results.filter((payment) => {
			const leaseId = payment.lease_id;
			return leaseId && propertyLeaseIds.includes(leaseId);
		});
	}, [paymentsData, propertyLeaseIds]);

	// Transform leases to units format
	const units = useMemo(() => {
		if (!leasesData?.results || !property) return [];
		try {
			return leasesData.results.map((lease) => ({
				id: lease.id,
				leaseId: lease.id,
				number:
					lease.property_name || property.name || property.address || "N/A",
				type: `${property.bedrooms ?? "N/A"}BR/${
					property.bathrooms ?? "N/A"
				}BA`,
				sqft: property.square_feet || 0,
				rent: typeof lease.monthly_rent === "number" ? lease.monthly_rent : 0,
				tenant: lease.tenant_name || "Unknown",
				tenantEmail: lease.tenant_email || undefined,
				status: lease.status === "active" ? "Occupied" : "Vacant",
				leaseEnd: lease.end_date || null,
				leaseStart: lease.start_date || null,
			}));
		} catch (error) {
			console.error("Error transforming leases to units:", error);
			return [];
		}
	}, [leasesData, property]);

	// Maintenance requests are already filtered by property in the API
	const maintenanceHistory = useMemo(() => {
		if (!maintenanceData?.results) return [];
		try {
			return maintenanceData.results
				.map((req: MaintenanceRequest) => ({
					id: req.id,
					date:
						req.requested_date || req.created_at || new Date().toISOString(),
					type: req.priority || "General",
					description: req.description || req.title || "No description",
					cost:
						typeof req.actual_cost === "number"
							? req.actual_cost
							: typeof req.estimated_cost === "number"
							? req.estimated_cost
							: 0,
					status:
						req.status === "completed"
							? "Completed"
							: req.status === "in_progress"
							? "In Progress"
							: "Pending",
					technician: req.assigned_to_name || "Unassigned",
				}))
				.sort((a, b) => {
					try {
						return new Date(b.date).getTime() - new Date(a.date).getTime();
					} catch {
						return 0;
					}
				});
		} catch (error) {
			console.error("Error processing maintenance history:", error);
			return [];
		}
	}, [maintenanceData]);

	// Calculate financial data from real payments
	const financialData = useMemo(() => {
		try {
			if (!propertyPayments || propertyPayments.length === 0) {
				return {
					monthlyRevenue: 0,
					expenses: { maintenance: 0 },
					netIncome: 0,
					ytdRevenue: 0,
					ytdExpenses: 0,
					ytdNetIncome: 0,
				};
			}

			// Calculate monthly revenue (paid payments this month)
			const currentMonth = new Date();
			currentMonth.setDate(1);
			currentMonth.setHours(0, 0, 0, 0);

			const monthlyRevenue = (propertyPayments || [])
				.filter((p: Payment) => {
					try {
						return (
							p.status === "paid" &&
							p.paid_date &&
							new Date(p.paid_date) >= currentMonth
						);
					} catch {
						return false;
					}
				})
				.reduce((sum: number, p: Payment) => {
					const amount = typeof p.amount === "number" ? p.amount : 0;
					return sum + amount;
				}, 0);

			// Calculate maintenance expenses (completed maintenance with actual cost)
			const maintenanceExpenses = maintenanceHistory
				.filter(
					(m: { status: string; cost: number }) =>
						m.status === "Completed" &&
						typeof m.cost === "number" &&
						m.cost > 0,
				)
				.reduce((sum: number, m: { cost: number }) => sum + m.cost, 0);

			// Calculate YTD revenue (all paid payments this year)
			const yearStart = new Date();
			yearStart.setMonth(0, 1);
			yearStart.setHours(0, 0, 0, 0);

			const ytdRevenue = (propertyPayments || [])
				.filter((p: Payment) => {
					try {
						return (
							p.status === "paid" &&
							p.paid_date &&
							new Date(p.paid_date) >= yearStart
						);
					} catch {
						return false;
					}
				})
				.reduce((sum: number, p: Payment) => {
					const amount = typeof p.amount === "number" ? p.amount : 0;
					return sum + amount;
				}, 0);

			// Calculate YTD expenses (maintenance costs this year)
			const ytdExpenses = maintenanceHistory
				.filter((m: { status: string; cost: number; date: string }) => {
					try {
						return (
							m.status === "Completed" &&
							typeof m.cost === "number" &&
							m.cost > 0 &&
							new Date(m.date) >= yearStart
						);
					} catch {
						return false;
					}
				})
				.reduce((sum: number, m: { cost: number }) => sum + m.cost, 0);

			return {
				monthlyRevenue,
				expenses: {
					maintenance: maintenanceExpenses,
				},
				netIncome: monthlyRevenue - maintenanceExpenses,
				ytdRevenue,
				ytdExpenses,
				ytdNetIncome: ytdRevenue - ytdExpenses,
			};
		} catch (error) {
			console.error("Error calculating financial data:", error);
			return {
				monthlyRevenue: 0,
				expenses: { maintenance: 0 },
				netIncome: 0,
				ytdRevenue: 0,
				ytdExpenses: 0,
				ytdNetIncome: 0,
			};
		}
	}, [propertyPayments, maintenanceHistory]);

	// Early return check - MUST be after all hooks
	if (!isOpen || !property) return null;

	// Safety check: ensure property has required fields
	if (!property.id) {
		console.error("Property missing required id field:", property);
		return null;
	}

	const tabs = [
		{ id: "overview", label: "Overview", icon: "Home" },
		{ id: "units", label: "Units", icon: "Building" },
		{ id: "tenants", label: "Tenants", icon: "Users" },
		{ id: "maintenance", label: "Maintenance", icon: "Wrench" },
		{ id: "financial", label: "Financial", icon: "Currency" },
	];

	const getStatusColor = (status: string): string => {
		switch (status) {
			case "Occupied":
				return "bg-success/10 text-success";
			case "Vacant":
				return "bg-muted text-muted-foreground";
			case "Maintenance":
				return "bg-warning/10 text-warning";
			case "Completed":
				return "bg-success/10 text-success";
			case "In Progress":
				return "bg-warning/10 text-warning";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	const renderOverview = () => {
		if (!property) return null;

		try {
			return (
				<div className="space-y-6">
					{/* Property Images Carousel */}
					{property.images && property.images.length > 0 ? (
						<div className="relative">
							<div className="relative h-64 rounded-lg overflow-hidden bg-muted">
								<Image
									src={property.images[currentImageIndex]}
									alt={`${
										property.address || property.name || "Property"
									} - Image ${currentImageIndex + 1}`}
									className="w-full h-full object-cover"
								/>

								{/* Navigation Arrows */}
								{property.images.length > 1 && (
									<>
										<button
											onClick={(e) => {
												e.stopPropagation();
												setCurrentImageIndex((prev) =>
													prev > 0 ? prev - 1 : property.images.length - 1,
												);
											}}
											className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition z-10"
											aria-label="Previous image"
										>
											<Icon name="ChevronLeft" size={20} />
										</button>
										<button
											onClick={(e) => {
												e.stopPropagation();
												setCurrentImageIndex((prev) =>
													prev < property.images.length - 1 ? prev + 1 : 0,
												);
											}}
											className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition z-10"
											aria-label="Next image"
										>
											<Icon name="ChevronRight" size={20} />
										</button>
									</>
								)}

								{/* Image Counter */}
								{property.images.length > 1 && (
									<div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/50 text-white text-xs font-medium z-10">
										{currentImageIndex + 1} / {property.images.length}
									</div>
								)}
							</div>

							{/* Thumbnail Strip */}
							{property.images.length > 1 && (
								<div className="mt-3 flex gap-2 overflow-x-auto pb-2">
									{property.images.map((image, index) => (
										<button
											key={index}
											onClick={() => setCurrentImageIndex(index)}
											className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 smooth-transition ${
												index === currentImageIndex
													? "border-primary"
													: "border-border hover:border-primary/50"
											}`}
											aria-label={`View image ${index + 1}`}
										>
											<Image
												src={image}
												alt={`Thumbnail ${index + 1}`}
												className="w-full h-full object-cover"
											/>
										</button>
									))}
								</div>
							)}
						</div>
					) : (
						<div className="h-64 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
							<Icon
								name="Building2"
								size={48}
								className="text-muted-foreground"
							/>
						</div>
					)}

					{/* Basic Information */}
					<div className="grid grid-cols-2 gap-4">
						<div>
							<h4 className="font-medium text-foreground mb-2">
								Property Details
							</h4>
							<div className="space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Type:</span>
									<span className="text-foreground capitalize">
										{property.property_type}
									</span>
								</div>
								{property.bedrooms !== undefined && (
									<div className="flex justify-between">
										<span className="text-muted-foreground">Bedrooms:</span>
										<span className="text-foreground">{property.bedrooms}</span>
									</div>
								)}
								{property.bathrooms !== undefined && (
									<div className="flex justify-between">
										<span className="text-muted-foreground">Bathrooms:</span>
										<span className="text-foreground">
											{property.bathrooms}
										</span>
									</div>
								)}
								{property.square_feet !== undefined && (
									<div className="flex justify-between">
										<span className="text-muted-foreground">Size:</span>
										<span className="text-foreground">
											{property.square_feet.toLocaleString()} sq ft
										</span>
									</div>
								)}
								{/* Show rent_amount (new field) or monthly_rent (legacy) */}
								{((property.rent_amount && property.rent_amount > 0) ||
									(property.monthly_rent && property.monthly_rent > 0)) && (
									<div className="flex justify-between">
										<span className="text-muted-foreground flex items-center gap-1">
											<Icon name="Key" size={14} className="text-blue-600" />
											Lease Price:
										</span>
										<span className="text-foreground">
											{formatCurrency(property.rent_amount || property.monthly_rent || 0)}
											<span className="text-xs text-muted-foreground ml-1">
												{property.rent_type
													? `/ ${property.rent_type === "annual" ? "year" : property.rent_type === "biannual" ? "6 months" : property.rent_type === "monthly" ? "month" : "week"}`
													: "/ month"}
											</span>
										</span>
									</div>
								)}
								{property.purchase_price && property.purchase_price > 0 && (
									<div className="flex justify-between">
										<span className="text-muted-foreground flex items-center gap-1">
											<Icon name="Tag" size={14} className="text-green-600" />
											Purchase Price:
										</span>
										<span className="text-foreground">
											{formatCurrency(property.purchase_price)}
										</span>
									</div>
								)}
							</div>
						</div>

						<div>
							<h4 className="font-medium text-foreground mb-2">Occupancy</h4>
							<div className="space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Total Units:</span>
									<span className="text-foreground">{units.length}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Occupied:</span>
									<span className="text-foreground">
										{units.filter((u) => u.status === "Occupied").length}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Vacant:</span>
									<span className="text-foreground">
										{units.filter((u) => u.status === "Vacant").length}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Rate:</span>
									<span className="text-foreground">
										{units.length > 0
											? Math.round(
													(units.filter((u) => u.status === "Occupied").length /
														units.length) *
														100,
											  )
											: 0}
										%
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Description */}
					{property.description && (
						<div>
							<h4 className="font-medium text-foreground mb-2">Description</h4>
							<p className="text-sm text-muted-foreground whitespace-pre-wrap">
								{property.description}
							</p>
						</div>
					)}

					{/* Features */}
					{property.features && property.features.length > 0 && (
						<div>
							<h4 className="font-medium text-foreground mb-2">Property Features</h4>
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
								{property.features.map((feature, index) => (
									<div
										key={index}
										className="flex items-center space-x-2 p-2 bg-muted/30 rounded-lg"
									>
										<Icon name="Check" size={14} className="text-primary flex-shrink-0" />
										<span className="text-sm text-foreground">{feature}</span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			);
		} catch (error) {
			console.error("Error rendering overview:", error);
			return (
				<div className="text-center py-8 text-muted-foreground">
					Error loading property overview
				</div>
			);
		}
	};

	const formatCurrency = (amount: number): string => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string | null | undefined): string => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const renderUnits = () => (
		<div className="space-y-4">
			{units.length === 0 ? (
				<div className="text-center py-8 text-muted-foreground">
					No leases found for this property
				</div>
			) : (
				units.map((unit) => (
					<div key={unit.id} className="bg-muted/30 rounded-lg p-4">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center space-x-3">
								<h4 className="font-medium text-foreground">{unit.number}</h4>
								<span
									className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
										unit.status,
									)}`}
								>
									{unit.status}
								</span>
							</div>
							<Button variant="ghost" size="sm" iconName="Edit" />
						</div>

						<div className="grid grid-cols-2 gap-4 text-sm">
							<div>
								<span className="text-muted-foreground">Type: </span>
								<span className="text-foreground">{unit.type}</span>
							</div>
							{unit.sqft > 0 && (
								<div>
									<span className="text-muted-foreground">Size: </span>
									<span className="text-foreground">
										{unit.sqft.toLocaleString()} sq ft
									</span>
								</div>
							)}
							{unit.rent > 0 && (
								<div>
									<span className="text-muted-foreground">Rent: </span>
									<span className="text-foreground">
										{formatCurrency(unit.rent)}/month
									</span>
								</div>
							)}
							<div>
								<span className="text-muted-foreground">Tenant: </span>
								<span className="text-foreground">
									{unit.tenant || "Vacant"}
								</span>
							</div>
						</div>

						{unit.leaseStart && (
							<div className="mt-2 text-sm">
								<span className="text-muted-foreground">Lease Start: </span>
								<span className="text-foreground">
									{formatDate(unit.leaseStart)}
								</span>
							</div>
						)}
						{unit.leaseEnd && (
							<div className="mt-2 text-sm">
								<span className="text-muted-foreground">Lease End: </span>
								<span className="text-foreground">
									{formatDate(unit.leaseEnd)}
								</span>
							</div>
						)}
					</div>
				))
			)}
		</div>
	);

	const renderTenants = () => {
		const occupiedUnits = units.filter((unit) => unit.status === "Occupied");

		// Calculate payment status for each tenant
		const getPaymentStatus = (leaseId: string) => {
			if (!propertyPayments || propertyPayments.length === 0) {
				return { status: "No Payments", color: "text-muted-foreground" };
			}

			// Get payments for this lease
			const leasePayments = propertyPayments.filter(
				(p: Payment) => p.lease_id === leaseId,
			);

			if (leasePayments.length === 0) {
				return { status: "No Payments", color: "text-muted-foreground" };
			}

			// Check for overdue payments (due date passed and not paid)
			const now = new Date();
			const overduePayments = leasePayments.filter((p: Payment) => {
				const dueDate = new Date(p.due_date);
				return dueDate < now && p.status !== "paid";
			});

			if (overduePayments.length > 0) {
				return { status: "Overdue", color: "text-destructive" };
			}

			// Check for pending payments
			const pendingPayments = leasePayments.filter(
				(p: Payment) => p.status === "pending",
			);

			if (pendingPayments.length > 0) {
				return { status: "Pending", color: "text-warning" };
			}

			// All payments are paid
			return { status: "Current", color: "text-success" };
		};

		return (
			<div className="space-y-4">
				{occupiedUnits.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						No active tenants for this property
					</div>
				) : (
					occupiedUnits.map((unit) => {
						const paymentStatus = getPaymentStatus(unit.leaseId || unit.id);
						return (
							<div key={unit.id} className="bg-muted/30 rounded-lg p-4">
								<div className="flex items-center justify-between mb-3">
									<div className="flex items-center space-x-3">
										<div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
											<Icon name="User" size={20} />
										</div>
										<div>
											<h4 className="font-medium text-foreground">
												{unit.tenant}
											</h4>
											<p className="text-sm text-muted-foreground">
												{unit.number}
											</p>
											{unit.tenantEmail && (
												<p className="text-xs text-muted-foreground">
													{unit.tenantEmail}
												</p>
											)}
										</div>
									</div>
									<Button variant="ghost" size="sm" iconName="MessageCircle" />
								</div>

								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<span className="text-muted-foreground">Rent: </span>
										<span className="text-foreground">
											{formatCurrency(unit.rent)}/month
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Lease End: </span>
										<span className="text-foreground">
											{formatDate(unit.leaseEnd)}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">
											Payment Status:{" "}
										</span>
										<span className={paymentStatus.color}>
											{paymentStatus.status}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Move-in: </span>
										<span className="text-foreground">
											{formatDate(unit.leaseStart)}
										</span>
									</div>
								</div>
							</div>
						);
					})
				)}
			</div>
		);
	};

	const renderMaintenance = () => (
		<div className="space-y-4">
			{maintenanceHistory.length === 0 ? (
				<div className="text-center py-8 text-muted-foreground">
					No maintenance history for this property
				</div>
			) : (
				maintenanceHistory.map((item) => (
					<div key={item.id} className="bg-muted/30 rounded-lg p-4">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center space-x-3">
								<div className="p-2 bg-primary/10 text-primary rounded-lg">
									<Icon name="Wrench" size={16} />
								</div>
								<div>
									<h4 className="font-medium text-foreground">{item.type}</h4>
									<p className="text-sm text-muted-foreground">
										{formatDate(item.date)}
									</p>
								</div>
							</div>
							<span
								className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
									item.status,
								)}`}
							>
								{item.status}
							</span>
						</div>

						<p className="text-sm text-foreground mb-3">{item.description}</p>

						<div className="grid grid-cols-2 gap-4 text-sm">
							<div>
								<span className="text-muted-foreground">Cost: </span>
								<span className="text-foreground">
									{formatCurrency(item.cost)}
								</span>
							</div>
							<div>
								<span className="text-muted-foreground">Technician: </span>
								<span className="text-foreground">{item.technician}</span>
							</div>
						</div>
					</div>
				))
			)}
		</div>
	);

	const renderFinancial = () => (
		<div className="space-y-6">
			{/* Monthly Summary */}
			<div className="bg-muted/30 rounded-lg p-4">
				<h4 className="font-medium text-foreground mb-4">Monthly Summary</h4>
				<div className="space-y-3">
					<div className="flex justify-between">
						<span className="text-muted-foreground">Gross Revenue:</span>
						<span className="text-foreground font-medium">
							{formatCurrency(financialData.monthlyRevenue)}
						</span>
					</div>
					<div className="flex justify-between">
						<span className="text-muted-foreground">Total Expenses:</span>
						<span className="text-foreground font-medium">
							{formatCurrency(financialData.expenses.maintenance)}
						</span>
					</div>
					<div className="border-t border-border pt-2">
						<div className="flex justify-between">
							<span className="text-foreground font-medium">Net Income:</span>
							<span className="text-success font-medium">
								{formatCurrency(financialData.netIncome)}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Expense Breakdown */}
			<div className="bg-muted/30 rounded-lg p-4">
				<h4 className="font-medium text-foreground mb-4">Monthly Expenses</h4>
				<div className="space-y-3">
					{Object.entries(financialData.expenses).map(([category, amount]) => (
						<div key={category} className="flex justify-between">
							<span className="text-muted-foreground capitalize">
								{category}:
							</span>
							<span className="text-foreground">{formatCurrency(amount)}</span>
						</div>
					))}
					{Object.keys(financialData.expenses).length === 0 && (
						<div className="text-sm text-muted-foreground">
							No expenses recorded
						</div>
					)}
				</div>
			</div>

			{/* Year-to-Date */}
			<div className="bg-muted/30 rounded-lg p-4">
				<h4 className="font-medium text-foreground mb-4">Year-to-Date</h4>
				<div className="space-y-3">
					<div className="flex justify-between">
						<span className="text-muted-foreground">YTD Revenue:</span>
						<span className="text-foreground font-medium">
							{formatCurrency(financialData.ytdRevenue)}
						</span>
					</div>
					<div className="flex justify-between">
						<span className="text-muted-foreground">YTD Expenses:</span>
						<span className="text-foreground font-medium">
							{formatCurrency(financialData.ytdExpenses)}
						</span>
					</div>
					<div className="border-t border-border pt-2">
						<div className="flex justify-between">
							<span className="text-foreground font-medium">
								YTD Net Income:
							</span>
							<span className="text-success font-medium">
								{formatCurrency(financialData.ytdNetIncome)}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	if (!isOpen || !property) return null;

	// Safety check: ensure property has required fields
	const safeProperty = property; // Type narrowing
	if (!safeProperty || !safeProperty.id) {
		console.error("Property missing required id field:", safeProperty);
		return null;
	}

	const renderTabContent = () => {
		try {
			switch (activeTab) {
				case "overview":
					return renderOverview();
				case "units":
					return renderUnits();
				case "tenants":
					return renderTenants();
				case "maintenance":
					return renderMaintenance();
				case "financial":
					return renderFinancial();
				default:
					return renderOverview();
			}
		} catch (error) {
			console.error("Error rendering tab content:", error);
			return (
				<div className="text-center py-8 text-muted-foreground">
					Error loading content. Please try again.
				</div>
			);
		}
	};

	try {
		return (
			<>
				{/* Backdrop */}
				<div
					className="fixed inset-0 bg-black/50 z-1100 animate-fade-in"
					onClick={onClose}
				/>

				{/* Panel */}
				<div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-card border-l border-border z-1200 animate-slide-in-right overflow-hidden flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-border">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								{property?.unit_number
									? `${property.unit_number}, ${property?.address || property?.name}`
									: property?.address || property?.name || "Property Details"}
							</h2>
							<p className="text-sm text-muted-foreground">
								{property?.city || ""}
								{property?.city && property?.state ? ", " : ""}
								{property?.state || ""}
							</p>
						</div>
						<div className="flex items-center space-x-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => property && onEdit(property)}
								iconName="Edit"
								iconPosition="left"
							>
								Edit
							</Button>
							<Button variant="ghost" size="icon" onClick={onClose}>
								<Icon name="X" size={20} />
							</Button>
						</div>
					</div>

					{/* Tabs */}
					<div className="flex border-b border-border">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 smooth-transition ${
									activeTab === tab.id
										? "border-primary text-primary"
										: "border-transparent text-muted-foreground hover:text-foreground"
								}`}
							>
								<Icon name={tab.icon as any} size={16} />
								<span>{tab.label}</span>
							</button>
						))}
					</div>

					{/* Content */}
					<div className="flex-1 overflow-y-auto p-6">{renderTabContent()}</div>
				</div>
			</>
		);
	} catch (error: any) {
		console.error("Error rendering PropertyDetailPanel:", error);
		return (
			<>
				<div
					className="fixed inset-0 bg-black/50 z-1100 animate-fade-in"
					onClick={onClose}
				/>
				<div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-card border-l border-border z-1200 animate-slide-in-right overflow-hidden flex flex-col">
					<div className="flex items-center justify-between p-6 border-b border-border">
						<h2 className="text-xl font-semibold text-foreground">Error</h2>
						<Button variant="ghost" size="icon" onClick={onClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>
					<div className="flex-1 overflow-y-auto p-6">
						<div className="text-center py-8 text-muted-foreground">
							<p>An error occurred while loading property details.</p>
							<p className="mt-2 text-sm">
								Please try again or contact support.
							</p>
						</div>
					</div>
				</div>
			</>
		);
	}
};

export default PropertyDetailPanel;
